import { useCallback, useEffect, useState, useContext } from "react";
import ProfileNavigation from "../../../components/common/ProfileNavigation";
import SEO from "../../../components/SEO";
import useUser, { getAuthorized, isUserClient, isUserDoctor } from "../../../hooks/User";
import { useNavigate } from "react-router-dom";
import { ErrorHandlerContext, ErrorHandlerContextProvider } from "../../../contexts/ErrorHandlerContext";
import ErrorHandler from "../../../components/common/ErrorHandler";
import { ROUTES } from "../../../routes";
import dayjs from "dayjs";
import "./patientsurveyhistory.css";

// Icons
const HistoryIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v5h5"/>
        <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/>
        <path d="M12 7v5l4 2"/>
    </svg>
);

const FileTextIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
        <polyline points="14 2 14 8 20 8"/>
    </svg>
);

const CheckCircleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
);

const ClockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
    </svg>
);

const ChevronRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m9 18 6-6-6-6"/>
    </svg>
);

const FilterIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
    </svg>
);

// Status configurations
const StatusConfig = {
    completed: { 
        label: "Завершён", 
        className: "status-completed",
        icon: <CheckCircleIcon />
    },
    in_progress: { 
        label: "В процессе", 
        className: "status-in-progress",
        icon: <ClockIcon />
    },
    draft: { 
        label: "Черновик", 
        className: "status-draft",
        icon: <FileTextIcon />
    },
    overdue: { 
        label: "Просрочен", 
        className: "status-overdue",
        icon: <ClockIcon />
    }
};

// Attempt Card Component
function AttemptCard({ attempt, onViewDetails }) {
    const status = StatusConfig[attempt.status] || StatusConfig.draft;
    const progress = attempt.total_questions_count > 0 
        ? Math.round((attempt.answered_questions_count / attempt.total_questions_count) * 100)
        : 0;

    return (
        <div className="attempt-card" onClick={() => onViewDetails(attempt)}>
            <div className="attempt-card-header">
                <div className={`attempt-status-badge ${status.className}`}>
                    {status.icon}
                    <span>{status.label}</span>
                </div>
                <span className="attempt-date">
                    {dayjs(attempt.creation_dt).format("DD.MM.YYYY HH:mm")}
                </span>
            </div>

            <div className="attempt-card-content">
                <h3 className="attempt-survey-title">
                    <FileTextIcon />
                    {attempt.survey_title}
                </h3>
                {attempt.survey_description && (
                    <p className="attempt-survey-description">{attempt.survey_description}</p>
                )}
            </div>

            <div className="attempt-card-footer">
                <div className="attempt-progress">
                    <div className="progress-bar">
                        <div 
                            className="progress-fill" 
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className="progress-text">
                        {attempt.answered_questions_count} / {attempt.total_questions_count} вопросов
                    </span>
                </div>
                <button className="btn-view-details">
                    Подробнее
                    <ChevronRightIcon />
                </button>
            </div>
        </div>
    );
}

// Filter Component
function AttemptFilters({ status, onStatusChange }) {
    const filters = [
        { value: "", label: "Все статусы" },
        { value: "completed", label: "Завершённые" },
        { value: "in_progress", label: "В процессе" },
        { value: "draft", label: "Черновики" },
        { value: "overdue", label: "Просроченные" }
    ];

    return (
        <div className="attempt-filters">
            <FilterIcon />
            <select 
                value={status} 
                onChange={(e) => onStatusChange(e.target.value)}
                className="filter-select"
            >
                {filters.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                ))}
            </select>
        </div>
    );
}

// Main Component
function PatientSurveyHistory() {
    const navigate = useNavigate();
    const { setErrorList } = useContext(ErrorHandlerContext);
    
    const [attempts, setAttempts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const PAGE_SIZE = 10;

    const loadAttempts = useCallback(async (reset = false) => {
        if (reset) {
            setPage(0);
            setAttempts([]);
        }
        
        setLoading(true);
        try {
            const params = {
                st: reset ? 0 : page * PAGE_SIZE,
                fn: (reset ? 0 : page * PAGE_SIZE) + PAGE_SIZE - 1,
            };
            
            if (statusFilter) {
                params.status = statusFilter;
            }

            const response = await getAuthorized("/client/my_attempts", { params });
            
            const newAttempts = response.data?.attempts || [];
            const totalCount = response.data?.total_count || 0;
            
            if (reset) {
                setAttempts(newAttempts);
            } else {
                setAttempts(prev => [...prev, ...newAttempts]);
            }
            
            const currentTotal = reset ? newAttempts.length : attempts.length + newAttempts.length;
            setHasMore(currentTotal < totalCount);
            
            if (!reset) {
                setPage(prev => prev + 1);
            }
        } catch (error) {
            console.error("Failed to load attempts:", error);
            setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при загрузке истории"]);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, page, attempts.length, setErrorList]);

    useEffect(() => {
        loadAttempts(true);
    }, [statusFilter]);

    const handleViewDetails = useCallback((attempt) => {
        navigate(ROUTES.SURVEY_ATTEMPT_DETAIL, {
            viewTransition: true,
            state: { attemptId: attempt.attempt_id }
        });
    }, [navigate]);

    const loadMore = () => {
        loadAttempts(false);
    };

    return (
        <div className="survey-history-container">
            <div className="history-header">
                <div className="history-title">
                    <HistoryIcon />
                    <div>
                        <h1>История опросов</h1>
                        <p className="history-subtitle">
                            Все ваши попытки прохождения опросов
                        </p>
                    </div>
                </div>
                <AttemptFilters 
                    status={statusFilter} 
                    onStatusChange={setStatusFilter}
                />
            </div>

            {loading && attempts.length === 0 ? (
                <div className="history-loading">
                    <div className="spinner"></div>
                    <p>Загрузка истории...</p>
                </div>
            ) : attempts.length === 0 ? (
                <div className="empty-history-state">
                    <div className="empty-icon">
                        <HistoryIcon />
                    </div>
                    <h3>История пуста</h3>
                    <p>
                        {statusFilter 
                            ? "Нет опросов с выбранным статусом"
                            : "Вы ещё не проходили ни одного опроса"
                        }
                    </p>
                </div>
            ) : (
                <div className="history-content">
                    <div className="attempts-grid">
                        {attempts.map(attempt => (
                            <AttemptCard 
                                key={attempt.attempt_id}
                                attempt={attempt}
                                onViewDetails={handleViewDetails}
                            />
                        ))}
                    </div>
                    
                    {hasMore && (
                        <div className="load-more-container">
                            <button 
                                className="btn-load-more"
                                onClick={loadMore}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <div className="spinner-small"></div>
                                        Загрузка...
                                    </>
                                ) : (
                                    "Загрузить ещё"
                                )}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Page Component
function PatientSurveyHistoryPage() {
    const { data, loading, error } = useUser();
    const navigate = useNavigate();

    useEffect(() => {
        if (loading) return;
        if (error) navigate(ROUTES.AUTH, { viewTransition: true });
        // Разрешаем доступ всем пользователям с ролью client (включая врачей)
        if (data && !isUserClient(data)) navigate(ROUTES.PROFILE, { viewTransition: true });
    }, [data, error, loading, navigate]);

    if (loading) {
        return (
            <>
                <ProfileNavigation isDoctor={false} />
                <div className="survey-history-page">
                    <div className="history-loading">
                        <div className="spinner"></div>
                        <p>Загрузка...</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <SEO title="История опросов" description="История пройденных медицинских опросов и их результаты" noindex={true} />
            <ErrorHandlerContextProvider>
                <ProfileNavigation isDoctor={isUserDoctor(data)} />
                <div className="survey-history-page">
                    <PatientSurveyHistory />
                </div>
                <ErrorHandler />
            </ErrorHandlerContextProvider>
        </>
    );
}

export default PatientSurveyHistoryPage;
